import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from 'src/app/providers/environment';
import get from 'lodash.get';
import { BehaviorSubject, map, tap } from 'rxjs';
import {
  FaqItem,
  LandingContent,
  ButtonLabel,
  DocumentPageResult,
  BlogPost,
  ArticleResponse,
  BlogsResponse,
} from 'src/app/types/contentful';
import { CONSTANTS } from 'src/app/providers/constants/constants.provider';

@Injectable({
  providedIn: 'root',
})
export class ContentfulService {
  env = inject(ENVIRONMENT);
  http = inject(HttpClient);
  constants = inject(CONSTANTS);

  private _buttonLabels$ = new BehaviorSubject<ButtonLabel[]>([]);

  getTermContent() {
    return this.http
      .post(
        this.env.contentfulEndpoint,
        {
          query: `
{
  ${this.constants.PRIVACY_TERM_CONTENT_CONTENFUL_QUERY_KEY}(limit: 1, where: {title: "TERMS AND CONDITIONS"}) {
    items {
      title,
      subtitle,
      content {
        json
      }
    }
  }
}
`,
        },
        {
          headers: {
            Authorization: `Bearer ${this.env.contentfulToken}`,
          },
        },
      )
      .pipe(
        map(
          (response) =>
            get(
              response,
              `data.${this.constants.PRIVACY_TERM_CONTENT_CONTENFUL_QUERY_KEY}.items[0]`,
            ) as unknown as DocumentPageResult,
        ),
      );
  }

  getPrivacyContent() {
    return this.http
      .post(
        this.env.contentfulEndpoint,
        {
          query: `
{
  ${this.constants.PRIVACY_TERM_CONTENT_CONTENFUL_QUERY_KEY}(limit: 1, where: {title: "PRIVACY POLICY"}) {
    items {
      title,
      subtitle,
      content {
        json
      }
    }
  }
}
`,
        },
        {
          headers: {
            Authorization: `Bearer ${this.env.contentfulToken}`,
          },
        },
      )
      .pipe(
        map(
          (response) =>
            get(
              response,
              `data.${this.constants.PRIVACY_TERM_CONTENT_CONTENFUL_QUERY_KEY}.items[0]`,
            ) as unknown as DocumentPageResult,
        ),
      );
  }

  getBlogs() {
    const body = {
      query:
        '{\n  blogCollection {\n    items {\n   sys {\n id\n publishedAt\n }\n   title\n      caption\n tag\n      banner {\n        url\n      }\n    }\n  }\n}\n',
    };
    return this.http
      .post<BlogsResponse>(this.env.contentfulEndpoint, body, {
        headers: {
          Authorization: `Bearer ${this.env.contentfulToken}`,
        },
      })
      .pipe(map((response) => get(response, 'data.blogCollection.items', []) as BlogPost[]));
  }

  getArticle(id: string) {
    const body = {
      query:
        '# query {\n#   blogCollection {\n#     items {\n#       title\n\n#       sys {\n#         id\n#         publishedAt\n#       }\n#     }\n#   }\n# }\n\nquery ($id: String!) {\n  blog(id: $id) {\n    title\n    banner {\n      url\n    }\n    caption\n    contentCollection {\n      items {\n        title\n        caption\n        banner {\n          url\n        }\n      }\n    }\n  }\n}',
      variables: { id },
    };
    return this.http.post<ArticleResponse>(this.env.contentfulEndpoint, body, {
      headers: {
        Authorization: `Bearer ${this.env.contentfulToken}`,
      },
    });
  }

  getButtonLabels() {
    const body = {
      query:
        '{\n  landingButtonLabelCollection {\n    items {\n      buttonId\n      buttonLabel\n      website    }\n  }\n}\n',
    };

    return this.http
      .post<{
        data: {
          landingButtonLabelCollection: {
            items: ButtonLabel[];
          };
        };
      }>(this.env.contentfulEndpoint, body, {
        headers: {
          Authorization: `Bearer ${this.env.contentfulToken}`,
        },
      })
      .pipe(
        map((response) =>
          response.data.landingButtonLabelCollection.items.filter((item) => item.website === 'Goodchart'),
        ),
        tap((labels) => {
          this._buttonLabels$.next(labels);
        }),
      );
  }

  getButtonLabel(buttonId: string) {
    if (!this._buttonLabels$.getValue().length) {
      this.getButtonLabels().subscribe();
      this._buttonLabels$.next([{ buttonId, buttonLabel: '', website: '' }]);
    }

    return this._buttonLabels$
      .asObservable()
      .pipe(map((labels) => labels.find((item) => item.buttonId === buttonId)?.buttonLabel ?? ''));
  }

  getLandingContent() {
    return this.http
      .post(
        this.env.contentfulEndpoint,
        {
          query: `
{
  gcLandingPageCollection(limit: 1) {
    items {
      titleBlocksCollection {
        items {
          name
          content
        }
      }
      contentBlocksCollection {
        items {
          name
          content
        }
      }
      buttonBlocksCollection {
        items {
          name
          content
        }
      }
      imageBlocksCollection {
        items {
          name
          image {
            url
          }
        }
      }
      imageLandingBlocksCollection {
        items {
          title
          content
          image {
            url
          }
        }
      }
      landingBlocksCollection {
        items {
          title
          content
        }
      }
      brandsCollection {
        items {
          title
          url
        }
      }
      videosCollection {
        items {
          title
          url
        }
      }
    }
  }
}
`,
        },
        {
          headers: {
            Authorization: `Bearer ${this.env.contentfulToken}`,
          },
        },
      )
      .pipe(
        map(
          (response) =>
            get(response, 'data.gcLandingPageCollection.items[0]') as unknown as Omit<
              LandingContent,
              'slidersCollection'
            >,
        ),
      );
  }

  getLandingSliderContent() {
    return this.http
      .post(
        this.env.contentfulEndpoint,
        {
          query: `
{
  gcLandingPageCollection(limit: 1) {
    items {
      slidersCollection {
        items {
          image {
            url
            title
          }
          sliderItemsCollection {
            items {
              name
              content
            }
          }
        }
      }
    }
  }
}
`,
        },
        {
          headers: {
            Authorization: `Bearer ${this.env.contentfulToken}`,
          },
        },
      )
      .pipe(
        map(
          (response) =>
            get(response, 'data.gcLandingPageCollection.items[0]') as unknown as Pick<
              LandingContent,
              'slidersCollection'
            >,
        ),
      );
  }

  getFaqContent() {
    return this.http
      .post(
        this.env.contentfulEndpoint,
        {
          query: `
{
  faqCollection(limit: 1000, order: [position_ASC]) {
    items {
      question
      position
      answer {
        json
      }
    }
  }
}
        `,
        },
        {
          headers: {
            Authorization: `Bearer ${this.env.contentfulToken}`,
          },
        },
      )
      .pipe(map((response) => get(response, 'data.faqCollection.items', []) as FaqItem[]));
  }
}
